import { useMemo } from 'react';

import { type DateRange } from '~/types/common';

import { dayjs } from '~/utils/datetime';
import { getDatesForPredefinedDateRange } from '~/utils/filters';
import { date4server } from '~/utils/l10n';

import { type DateRangeOption } from '~/ui/molecules/DateInput';

import { useUniversalFiltersSelector } from '../hooks/useUniversalFiltersSelector';
import { useUniversalFiltersStore } from '../hooks/useUniversalFiltersStore';
import { isValidManualFilterDate } from '../manualDateRange';
import { toPredefinedDateRangeOption } from '../predefinedDateRange';
import {
  type FilterDraftValue,
  UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
  UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
  type UniversalDateFieldConfig,
} from '../types';

export function usePrimaryDateFilter(
  dateField: UniversalDateFieldConfig | undefined,
) {
  const store = useUniversalFiltersStore();
  const draft = useUniversalFiltersSelector((context) => context.draft);

  const { dateRange, predefinedDateRange } = useMemo(() => {
    if (!dateField) {
      return {
        dateRange: [undefined, undefined] satisfies DateRange,
        predefinedDateRange: undefined,
      };
    }

    const predefinedValue = draft.find(
      (clause) =>
        clause.field === UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
    )?.value;
    const draftPredefinedRange = toPredefinedDateRangeOption(predefinedValue);

    if (draftPredefinedRange) {
      // A saved predefined range wins over any resolved manual date range.
      return {
        dateRange: getDatesForPredefinedDateRange(
          draftPredefinedRange,
          'date',
        ) as DateRange,
        predefinedDateRange: draftPredefinedRange,
      };
    }

    const draftValue = draft.find(
      (clause) => clause.field === UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
    )?.value;

    if (isNullableDateRange(draftValue)) {
      // Manual ranges are stored on the reserved universal date field.
      return {
        dateRange: toDateRange(draftValue),
        predefinedDateRange: undefined,
      };
    }

    if (dateField.defaultPredefinedRange) {
      return {
        dateRange: getDatesForPredefinedDateRange(
          dateField.defaultPredefinedRange,
          'date',
        ) as DateRange,
        predefinedDateRange: dateField.defaultPredefinedRange,
      };
    }

    return {
      dateRange: [undefined, undefined] satisfies DateRange,
      predefinedDateRange: undefined,
    };
  }, [dateField, draft]);

  function handleDateRangeChange(nextDateRange: DateRange) {
    if (!dateField) return;
    if (!isCompleteDateRange(nextDateRange)) return;

    const nextValue = toNullableDateRange(nextDateRange);
    const [from, to] = nextValue;

    // MUI's range field publishes onChange per keystroke, so invalid Dates
    // (formatted to the literal "Invalid Date"), mid-typing years (`0202` while
    // typing `2025`) and momentarily inverted ranges all arrive here. Keep the
    // previous stored range until the entry is a plausible calendar range: an
    // inverted one would otherwise map to no date filter at all, silently
    // widening both the list and the export while the control still shows a
    // range (DNI-839).
    if (!isValidManualFilterDate(from) || !isValidManualFilterDate(to)) return;
    if (from > to) return;

    store.trigger.editSelectedDraftClause({
      field: UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
      value: nextValue,
    });

    store.trigger.removeSelectedDraftClause({
      field: UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
    });
  }

  function handlePredefinedDateRangeChange(
    nextPredefinedDateRange: DateRangeOption | 'individual' | undefined,
  ) {
    if (!dateField) return;

    if (!nextPredefinedDateRange) {
      store.trigger.removeSelectedDraftClause({
        field: UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
      });
      return;
    }

    store.trigger.removeSelectedDraftClause({
      field: UNIVERSAL_FILTER_PRIMARY_DATE_FIELD,
    });

    store.trigger.editSelectedDraftClause({
      field: UNIVERSAL_FILTER_PRIMARY_PREDEFINED_DATE_RANGE_FIELD,
      value: nextPredefinedDateRange,
    });
  }

  return {
    dateRange,
    handleDateRangeChange,
    handlePredefinedDateRangeChange,
    predefinedDateRange,
  };
}

type CompleteDateRange = [Date, Date];
type StoredDateRange = [string, string];

function toNullableDateRange(dateRange: CompleteDateRange): StoredDateRange {
  return [
    dayjs(dateRange[0]).format(date4server),
    dayjs(dateRange[1]).format(date4server),
  ];
}

function toDateRange(dateRange: StoredDateRange): DateRange {
  return dateRange.map((date) => dayjs(date).toDate()) as DateRange;
}

function isNullableDateRange(
  value: FilterDraftValue | undefined,
): value is StoredDateRange {
  return (
    Array.isArray(value) &&
    value.length === 2 &&
    value.every((item) => typeof item === 'string')
  );
}

function isCompleteDateRange(
  dateRange: DateRange,
): dateRange is CompleteDateRange {
  return dateRange[0] instanceof Date && dateRange[1] instanceof Date;
}
